// Automations (SPEC §11, §15, §16; route #/automations[/<tab>]): a drawer with two tabs.
// - Routines: the shared routines list (automations/routinelist.js) grouped by floor and department
//   or by agent (« Par agent »), with floor and agent filters and search; next run, last run (link to
//   its task), runs count, active switch, LANCER (run now), edit, delete; editor with the French
//   phrase, schedule builder, autonomy and output format (automations/routineeditor.js).
// - Déclencheurs n8n: enabled switch, agent, calls count, last call, secret hint; editor with the
//   template preview, callback URL and secret (automations/triggereditor.js); the secret shown once
//   after creation / rotation, "Intégrer dans n8n" help, TEST with a sample payload.
// Live: 'routine', 'routine.removed' (in the routines list), 'trigger', 'trigger.removed' events.
import { h, button, iconButton, switchToggle, tabs, badge, emptyState, spinner, setText, segmented } from '../dom.js';
import { t } from '../i18n/fr.js';
import { deptColor, relativeTime, truncate } from '../format.js';
import { sortedFloors, can as canIn } from '../store.js';
import {
  toList, upsertById, removeById, groupByDept, sortTriggers, agentGroups, officeIndex, agentsWithItems,
} from './automations/model.js';
import { maskedHint } from './automations/template.js';
import { createRoutineList, autonomyBadge } from './automations/routinelist.js';
import { openTriggerEditor, openSecretReveal, openIntegration, openTriggerTest, forgetSample } from './automations/triggereditor.js';
import { childLayers } from './automations/widgets.js';
import './automations/strings.js';

export { autonomyBadge };

const TABS = ['routines', 'triggers'];
const SEARCH_DEBOUNCE_MS = 150;

/**
 * The host part of a callback URL for the list ("127.0.0.1:5678"), '' when invalid.
 * @param {string} url
 */
export function callbackHost(url) {
  try {
    const u = new URL(String(url || ''));
    return u.host + (u.pathname && u.pathname !== '/' ? truncate(u.pathname, 28) : '');
  } catch {
    return '';
  }
}

/**
 * Opens the automations drawer.
 * @param {object} ctx app context
 * @param {'routines'|'triggers'|null} [tab] initial tab (default 'routines')
 * @returns {import('./overlay.js').Layer}
 */
export function openAutomations(ctx, tab = 'routines') {
  const st = {
    tab: TABS.includes(tab) ? tab : 'routines',
    floorId: '',
    agentId: '',
    group: 'dept',
    query: '',
    triggers: [], triggersStatus: 'loading', triggersError: '',
  };
  let layer = null;
  let children = null;
  let routines = null;
  let searchTimer = null;
  let triggersSeq = 0;
  const offs = [];
  const can = (need, scope) => canIn(ctx.store.get(), need, scope);
  const now = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const fk = (el, key) => { el.dataset.fk = key; return el; };

  // --- chrome ------------------------------------------------------------------------------------------
  const tabBar = tabs([
    { id: 'routines', label: t('automations.tabs.routines'), count: null },
    { id: 'triggers', label: t('automations.tabs.triggers'), count: null },
  ], { active: st.tab, label: t('automations.title'), onChange: (id) => { st.tab = id; render(); } });
  tabBar.classList.add('auto-tabs');

  const searchInput = h('input', {
    type: 'search', class: 'input input-sm auto-search', placeholder: t('auto.search'), 'aria-label': t('auto.searchLabel'),
    autocomplete: 'off', spellcheck: false,
  });
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { st.query = searchInput.value.trim(); render(); }, SEARCH_DEBOUNCE_MS);
  });
  const floorSelect = h('select', { class: 'input select input-sm auto-floor', 'aria-label': t('auto.floorLabel') });
  floorSelect.addEventListener('change', () => { st.floorId = floorSelect.value; render(); });
  // « Agent » filter and « Par service / Par agent » grouping (routines only).
  const agentSelect = h('select', { class: 'input select input-sm auto-agent', 'aria-label': t('auto.agentLabel') });
  agentSelect.addEventListener('change', () => { st.agentId = agentSelect.value; render(); });
  const groupSeg = segmented([
    { value: 'dept', label: t('auto.group.dept') },
    { value: 'agent', label: t('auto.group.agent') },
  ], { value: st.group, size: 'sm', label: t('auto.groupLabel'), onChange: (v) => { st.group = v; render(); } });
  groupSeg.classList.add('auto-group-switch');
  const newBtnHost = h('div', { class: 'auto-newhost' });
  const calendarBtn = button(t('auto.calendar'), { variant: 'ghost', size: 'sm', icon: 'calendar', onClick: () => ctx.navigate('#/calendar') });
  const toolbar = h('div', { class: 'auto-toolbar' }, searchInput, floorSelect, agentSelect, groupSeg, h('span', { class: 'spacer' }), calendarBtn, newBtnHost);
  const intro = h('p', { class: 'auto-intro muted' });
  const listHost = h('div', { class: 'auto-listhost', role: 'tabpanel' });
  const root = h('div', { class: 'auto-root' }, tabBar, toolbar, intro, listHost);

  // --- helpers -----------------------------------------------------------------------------------------
  const office = () => ctx.store.get().office || {};
  const managedGroups = () => agentGroups(ctx.store.get(), 'manage');

  function renderFloors() {
    const floors = sortedFloors(office().floors || []);
    floorSelect.replaceChildren(h('option', { value: '' }, t('auto.floorAll')), ...floors.map((f) => h('option', { value: f.id }, f.name)));
    if (st.floorId && !floors.some((f) => f.id === st.floorId)) st.floorId = '';
    floorSelect.value = st.floorId;
    floorSelect.hidden = floors.length < 2;
  }

  /** The « Agent » filter lists the agents that have routines (on the chosen floor). */
  function renderAgents(routinesTab) {
    const items = (routines?.items || []).filter((r) => !st.floorId || r.floorId === st.floorId);
    const agents = agentsWithItems(items, office());
    if (st.agentId && !agents.some((a) => a.id === st.agentId)) st.agentId = '';
    agentSelect.replaceChildren(h('option', { value: '' }, t('auto.agentAll')), ...agents.map((a) => h('option', { value: a.id }, a.label)));
    agentSelect.value = st.agentId;
    agentSelect.hidden = !routinesTab || agents.length < 2;
    groupSeg.hidden = !routinesTab || agents.length < 2;
  }

  function stateBlock(status, error, retry) {
    if (status === 'loading') return h('div', { class: 'auto-state' }, spinner(t('auto.loading')), h('span', { class: 'muted' }, t('auto.loading')));
    if (status === 'unavailable') return emptyState({ icon: 'info', title: t('common.soon'), text: t('auto.unavailable') });
    if (status === 'error') {
      return emptyState({
        icon: 'alert', title: t('common.error'), text: error,
        action: button(t('auto.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: retry }),
      });
    }
    return null;
  }

  function groupHeader(g) {
    return h('div', { class: 'auto-group-head' },
      h('span', { class: 'auto-dot', style: { '--dept': deptColor(g.color, g.deptId) }, 'aria-hidden': 'true' }),
      h('h3', { class: 'auto-group-title' }, [g.floorName, g.deptName].filter(Boolean).join(' · ') || t('auto.none')),
      h('span', { class: 'auto-group-count mono-label' }, String(g.items.length)));
  }

  function metaItem(label, value) {
    return h('div', { class: 'auto-meta-item' }, h('span', { class: 'mono-label' }, label), h('span', { class: 'auto-meta-value' }, value));
  }

// --- triggers --------------------------------------------------------------------------------------------
  function triggerRow(tr, idx) {
    const agent = idx.agents.get(tr.agentId);
    const [autLabel, autTone] = autonomyBadge(tr.autonomy);
    let toggle = null;
    toggle = switchToggle(t('auto.triggers.enabledLabel'), { checked: !!tr.enabled, onChange: (v) => setEnabled(tr, v, toggle) });
    toggle.input.setAttribute('aria-label', t('auto.triggers.toggleAria', { name: tr.name || tr.id }));
    fk(toggle.input, `t:${tr.id}:toggle`);
    toggle.classList.add('auto-switch');
    const hint = maskedHint(tr.secretHint);
    const host = tr.callback?.url ? callbackHost(tr.callback.url) : '';
    const testBtn = fk(button(t('auto.triggers.test'), { variant: 'secondary', size: 'sm', icon: 'send', onClick: () => children.add(openTriggerTest(ctx, tr)) }), `t:${tr.id}:test`);
    const intBtn = fk(button(t('auto.triggers.integrate'), { variant: 'ghost', size: 'sm', icon: 'layers', onClick: () => children.add(openIntegration(ctx, tr)) }), `t:${tr.id}:int`);
    let moreBtn = null;
    moreBtn = fk(iconButton('dots', `${t('auto.more')} — ${tr.name || tr.id}`, {
      variant: 'ghost',
      onClick: () => ctx.overlay.menu(moreBtn, [
        { label: t('auto.triggers.edit'), icon: 'settings', onSelect: () => editTrigger(tr) },
        { label: t('auto.triggers.rotate'), icon: 'key', onSelect: () => rotateSecret(tr) },
        { separator: true },
        { label: t('auto.triggers.delete'), icon: 'close', danger: true, onSelect: () => deleteTrigger(tr) },
      ], { label: t('auto.more') }),
    }), `t:${tr.id}:more`);
    moreBtn.setAttribute('aria-haspopup', 'menu');

    return h('li', { class: ['auto-item', { 'is-paused': !tr.enabled }], style: { '--dept': deptColor(idx.depts.get(tr.deptId)?.color, tr.deptId) } },
      h('div', { class: 'auto-item-main' },
        h('div', { class: 'auto-item-head' },
          h('h4', { class: 'auto-item-title' }, tr.name || tr.id),
          h('div', { class: 'auto-badges' },
            tr.enabled ? null : badge(t('auto.triggers.disabled'), 'muted'),
            badge(autLabel, autTone),
            tr.outputFormat === 'json' ? badge(t('auto.badge.json'), 'info') : null)),
        h('p', { class: 'auto-item-desc' }, [agent ? agent.name : t('auto.unknownAgent'), truncate(String(tr.promptTemplate || '').split('\n').find((l) => l.trim()) || '', 140)].filter(Boolean).join(' · ')),
        h('div', { class: 'auto-meta' },
          metaItem(t('auto.triggers.fires'), String(Number(tr.fires) || 0)),
          metaItem(t('auto.triggers.lastFired'), tr.lastFiredAt ? relativeTime(tr.lastFiredAt, now()) : t('auto.never')),
          metaItem(t('auto.triggers.secret'), tr.hasSecret === false ? t('auto.triggers.noSecret') : (hint || '••••')),
          metaItem(t('auto.triggers.callback'), host || t('auto.triggers.noCallback')))),
      h('div', { class: 'auto-item-side' }, toggle, h('div', { class: 'auto-actions' }, testBtn, intBtn,
        fk(button(t('auto.triggers.edit'), { variant: 'ghost', size: 'sm', onClick: () => editTrigger(tr) }), `t:${tr.id}:edit`), moreBtn)));
  }

  function renderTriggers() {
    const block = stateBlock(st.triggersStatus, st.triggersError, loadTriggers);
    if (block) return block;
    const idx = officeIndex(office());
    const groups = groupByDept(sortTriggers(st.triggers), office(), {
      floorId: st.floorId, query: st.query, text: (tr) => [tr.name, tr.promptTemplate, tr.callback?.url],
    });
    if (!st.triggers.length) {
      const canCreate = managedGroups().length > 0;
      return emptyState({
        icon: 'bolt', title: t('auto.triggers.emptyTitle'),
        text: canCreate ? t('auto.triggers.emptyText') : t('auto.triggers.emptyReadOnly'),
        action: canCreate ? button(t('auto.triggers.create'), { variant: 'primary', size: 'sm', icon: 'plus', onClick: () => editTrigger(null) }) : null,
      });
    }
    if (!groups.length) return emptyState({ icon: 'search', title: t('auto.noResults') });
    return h('div', { class: 'auto-groups' }, groups.map((g) => h('section', { class: 'auto-group', 'aria-label': [g.floorName, g.deptName].filter(Boolean).join(' · ') },
      groupHeader(g), h('ul', { class: 'auto-list' }, g.items.map((tr) => triggerRow(tr, idx))))));
  }

  async function setEnabled(tr, enabled, toggle) {
    toggle.input.disabled = true;
    try {
      const res = await ctx.api.patch(`/api/triggers/${encodeURIComponent(tr.id)}`, { enabled });
      const saved = res?.trigger && typeof res.trigger === 'object' ? res.trigger : res;
      st.triggers = upsertById(st.triggers, saved?.id ? saved : { ...tr, enabled });
      ctx.toast.success(enabled ? t('auto.triggers.enabledToast') : t('auto.triggers.disabledToast'));
    } catch (err) {
      toggle.input.checked = !!tr.enabled;
      ctx.toast.error(err);
    } finally {
      toggle.input.disabled = false;
    }
    render();
  }

  function editTrigger(tr) {
    children.add(openTriggerEditor(ctx, {
      trigger: tr,
      // A new trigger shows the inbox path of the list (every trigger carries the same one).
      inboxDir: (st.triggers || []).find((x) => typeof x?.inboxDir === 'string' && x.inboxDir)?.inboxDir || '',
      onSaved: (saved, secret) => {
        if (saved?.id) st.triggers = upsertById(st.triggers, saved);
        render();
        if (secret && saved?.id) children.add(openSecretReveal(ctx, { trigger: saved, secret }));
      },
    }));
  }

  async function rotateSecret(tr) {
    const ok = await ctx.overlay.confirm({
      title: t('auto.triggers.rotateTitle'), message: t('auto.triggers.rotateText'),
      confirmLabel: t('auto.triggers.rotateConfirm'), danger: true,
    });
    if (!ok) return;
    try {
      const res = await ctx.api.post(`/api/triggers/${encodeURIComponent(tr.id)}/rotate-secret`, {});
      const saved = res?.trigger && typeof res.trigger === 'object' ? res.trigger : tr;
      st.triggers = upsertById(st.triggers, saved);
      render();
      if (typeof res?.secret === 'string' && res.secret) children.add(openSecretReveal(ctx, { trigger: saved, secret: res.secret, rotated: true }));
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  async function deleteTrigger(tr) {
    const ok = await ctx.overlay.confirm({
      title: t('auto.triggers.deleteTitle'), message: t('auto.triggers.deleteText', { name: tr.name || tr.id }),
      confirmLabel: t('auto.triggers.delete'), danger: true,
    });
    if (!ok) return;
    try {
      await ctx.api.del(`/api/triggers/${encodeURIComponent(tr.id)}`);
      forgetSample(tr.id); // it may hold a real payload (customer data); the id could be reused later
      st.triggers = removeById(st.triggers, tr.id);
      ctx.toast.success(t('auto.triggers.deleted'));
      render();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  // --- render ----------------------------------------------------------------------------------------------
  let rendering = false;
  function render() {
    if (!layer?.isOpen() || rendering) return;
    rendering = true;
    try {
      const active = globalThis.document?.activeElement;
      const focusKey = active?.dataset?.fk || null;
      const routinesTab = st.tab === 'routines';
      tabBar.setActive(st.tab);
      tabBar.setCount('routines', routines?.status === 'ok' ? routines.items.length : null);
      tabBar.setCount('triggers', st.triggersStatus === 'ok' ? st.triggers.length : null);
      renderFloors();
      renderAgents(routinesTab);
      const canCreate = managedGroups().length > 0;
      newBtnHost.replaceChildren(...(canCreate
        ? [fk(button(routinesTab ? t('auto.routines.new') : t('auto.triggers.new'), {
          variant: 'primary', size: 'sm', icon: 'plus', onClick: () => (routinesTab ? routines?.create() : editTrigger(null)),
        }), `new:${st.tab}`)]
        : []));
      calendarBtn.hidden = !routinesTab;
      setText(intro, routinesTab ? '' : t('auto.triggers.intro'));
      intro.hidden = routinesTab;
      if (routinesTab) {
        routines?.setFilter({ floorId: st.floorId, agentId: st.agentId, query: st.query, group: st.group });
        listHost.replaceChildren(...(routines ? [routines.el] : []));
      } else listHost.replaceChildren(renderTriggers());
      listHost.setAttribute('aria-label', routinesTab ? t('automations.tabs.routines') : t('automations.tabs.triggers'));
      if (focusKey) {
        const again = [...root.querySelectorAll('[data-fk]')].find((el) => el.dataset.fk === focusKey);
        again?.focus?.();
      }
    } finally {
      rendering = false;
    }
  }

  // --- data ------------------------------------------------------------------------------------------------
  async function loadTriggers() {
    const seq = ++triggersSeq;
    st.triggersStatus = 'loading';
    render();
    try {
      const res = await ctx.api.get('/api/triggers');
      if (seq !== triggersSeq || !layer?.isOpen()) return;
      st.triggers = toList(res, 'triggers');
      st.triggersStatus = 'ok';
    } catch (err) {
      if (seq !== triggersSeq || !layer?.isOpen()) return;
      st.triggersStatus = err?.status === 404 ? 'unavailable' : err?.status === 403 ? 'ok' : 'error';
      st.triggers = [];
      st.triggersError = err?.message || t('common.error');
    }
    render();
  }

  // --- open ----------------------------------------------------------------------------------------------
  layer = ctx.overlay.openDrawer({
    title: t('automations.title'), eyebrow: t('auto.eyebrow'), subtitle: t('auto.subtitle'), size: 'xl',
    className: 'auto-drawer', content: root,
  });
  children = childLayers(layer);
  // The routines list keeps its own data and live events; its changes refresh the counts and filters.
  routines = createRoutineList(ctx, { group: st.group, onChange: () => render() });

  offs.push(ctx.events.on('trigger', (d) => {
    const tr = d?.trigger && typeof d.trigger === 'object' ? d.trigger : d;
    if (!tr?.id || st.triggersStatus !== 'ok') return;
    // The list is limited to triggers the user manages (same rule as GET /api/triggers).
    if (!can('manage', { floorId: tr.floorId, deptId: tr.deptId })) { st.triggers = removeById(st.triggers, tr.id); render(); return; }
    st.triggers = upsertById(st.triggers, tr);
    render();
  }));
  offs.push(ctx.events.on('trigger.removed', (d) => {
    if (!d?.id) return;
    st.triggers = removeById(st.triggers, d.id);
    render();
  }));
  offs.push(ctx.events.on('hello', () => { loadTriggers(); }));
  offs.push(ctx.store.on('office', () => render()));

  let minuteTimer = setInterval(() => { if (st.tab === 'triggers') render(); }, 60_000);
  layer.onClose(() => {
    for (const off of offs) { try { off?.(); } catch { /* ignore */ } }
    clearTimeout(searchTimer);
    clearInterval(minuteTimer);
    minuteTimer = null;
    routines?.destroy();
    triggersSeq += 1;
  });

  render();
  loadTriggers();
  return layer;
}
